import type { ProfileRecord } from "@/types/records";
import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { WebhooksManager } from "@/components/producer/WebhooksManager";
import { useAuth } from "@/contexts/AuthContext";
import { updateProfile } from "@/lib/api";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { DocumentInput } from "@/components/DocumentInput";
import { validateDocument, detectDocumentType, sanitizeDocument } from "@/utils/document";
import { AnimatedThemeToggler } from "@/components/ui/animated-theme-toggler";
import { Switch } from "@/components/ui/switch";
import {
  Upload,
  Loader2,
  Camera,
  ImageIcon,
  ArrowUpRight,
  User,
  Globe,
  Link,
  Instagram,
  Facebook,
  Smartphone,
  CalendarDays,
  MapPin,
  Palette,
  Bell,
  Mail,
  Send,
  CheckCircle2,
} from "lucide-react";
import { convertImageToWebP } from "@/lib/image";
import { cn } from "@/lib/utils";
import {
  panelPage,
  panelTitle,
  panelSurface,
  panelEyebrow,
  panelPill,
  panelRow,
  panelControl,
  panelSectionTitle,
  panelAccentDot,
  panelSectionHeader,
  panelFieldLabel,
} from "@/lib/panel-ui";

const PHONE_WIDTH = 433;
const PHONE_HEIGHT = 882;
const SCREEN_X = 21.25;
const SCREEN_Y = 19.25;
const SCREEN_WIDTH = 389.5;
const SCREEN_HEIGHT = 843.5;
const SCREEN_RADIUS = 55.75;

const LEFT_PCT = (SCREEN_X / PHONE_WIDTH) * 100;
const TOP_PCT = (SCREEN_Y / PHONE_HEIGHT) * 100;
const WIDTH_PCT = (SCREEN_WIDTH / PHONE_WIDTH) * 100;
const HEIGHT_PCT = (SCREEN_HEIGHT / PHONE_HEIGHT) * 100;
const RADIUS_H = (SCREEN_RADIUS / SCREEN_WIDTH) * 100;
const RADIUS_V = (SCREEN_RADIUS / SCREEN_HEIGHT) * 100;

interface IphoneProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
}

function Iphone({
  children,
  className,
  style,
  ...props
}: IphoneProps) {
  return (
    <div
      className={`relative inline-block w-full align-middle leading-none ${className}`}
      style={{
        aspectRatio: `${PHONE_WIDTH}/${PHONE_HEIGHT}`,
        borderRadius: "14% / 7%",
        overflow: "hidden",
        backgroundColor: "transparent",
        ...style,
      }}
      {...props}
    >
      <div
        className="absolute z-0 overflow-hidden bg-background"
        style={{
          left: `${LEFT_PCT}%`,
          top: `${TOP_PCT}%`,
          width: `${WIDTH_PCT}%`,
          height: `${HEIGHT_PCT}%`,
          borderRadius: `${RADIUS_H}% / ${RADIUS_V}%`,
        }}
      >
        {children}
      </div>

      <svg
        viewBox={`0 0 ${PHONE_WIDTH} ${PHONE_HEIGHT}`}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="absolute inset-0 size-full pointer-events-none"
        style={{ transform: "translateZ(0)" }}
      >
        <g mask="url(#screenPunch)">
          {/* Outer Border (Rim) - Black/Dark Charcoal */}
          <path
            d="M2 73C2 32.6832 34.6832 0 75 0H357C397.317 0 430 32.6832 430 73V809C430 849.317 397.317 882 357 882H75C34.6832 882 2 849.317 2 809V73Z"
            className="fill-[#1e1e1e]"
          />
          {/* Side Buttons - Dark Charcoal */}
          <path
            d="M0 171C0 170.448 0.447715 170 1 170H3V204H1C0.447715 204 0 203.552 0 203V171Z"
            className="fill-[#1e1e1e]"
          />
          <path
            d="M1 234C1 233.448 1.44772 233 2 233H3.5V300H2C1.44772 300 1 299.552 1 299V234Z"
            className="fill-[#1e1e1e]"
          />
          <path
            d="M1 319C1 318.448 1.44772 318 2 318H3.5V385H2C1.44772 385 1 384.552 1 384V319Z"
            className="fill-[#1e1e1e]"
          />
          <path
            d="M430 279H432C432.552 279 433 279.448 433 280V384C433 384.552 432.552 385 432 385H430V279Z"
            className="fill-[#1e1e1e]"
          />
          {/* Inner body edge bezel - Black/Charcoal */}
          <path
            d="M6 74C6 35.3401 37.3401 4 76 4H356C394.66 4 426 35.3401 426 74V808C426 846.66 394.66 878 356 878H76C37.3401 878 6 846.66 6 808V74Z"
            className="fill-[#09090b]"
          />
        </g>

        {/* Top antenna band */}
        <path
          opacity="0.5"
          d="M174 5H258V5.5C258 6.60457 257.105 7.5 256 7.5H176C174.895 7.5 174 6.60457 174 5.5V5Z"
          className="fill-[#262626]"
        />

        {/* Screen Bezel spacer - Dark Charcoal */}
        <path
          d={`M${SCREEN_X} 75C${SCREEN_X} 44.2101 46.2101 ${SCREEN_Y} 77 ${SCREEN_Y}H355C385.79 ${SCREEN_Y} 410.75 44.2101 410.75 75V807C410.75 837.79 385.79 862.75 355 862.75H77C46.2101 862.75 ${SCREEN_X} 837.79 ${SCREEN_X} 807V75Z`}
          className="fill-[#1e1e1e] stroke-[#1e1e1e] stroke-[0.5]"
          mask="url(#screenPunch)"
        />

        {/* Dynamic Island / Notch container */}
        <path
          d="M154 48.5C154 38.2827 162.283 30 172.5 30H259.5C269.717 30 278 38.2827 278 48.5C278 58.7173 269.717 67 259.5 67H172.5C162.283 67 154 58.7173 154 48.5Z"
          className="fill-[#171717]"
        />
        <path
          d="M249 48.5C249 42.701 253.701 38 259.5 38C265.299 38 270 42.701 270 48.5C270 54.299 265.299 59 259.5 59C253.701 59 249 54.299 249 48.5Z"
          className="fill-[#171717]"
        />
        <path
          d="M254 48.5C254 45.4624 256.462 43 259.5 43C262.538 43 265 45.4624 265 48.5C265 51.5376 262.538 54 259.5 54C256.462 54 254 51.5376 254 48.5Z"
          className="fill-[#262626]"
        />

        <defs>
          <mask id="screenPunch" maskUnits="userSpaceOnUse">
            <rect
              x="0"
              y="0"
              width={PHONE_WIDTH}
              height={PHONE_HEIGHT}
              fill="white"
            />
            <rect
              x={SCREEN_X}
              y={SCREEN_Y}
              width={SCREEN_WIDTH}
              height={SCREEN_HEIGHT}
              rx={SCREEN_RADIUS}
              ry={SCREEN_RADIUS}
              fill="black"
            />
          </mask>
          <clipPath id="roundedCorners">
            <rect
              x={SCREEN_X}
              y={SCREEN_Y}
              width={SCREEN_WIDTH}
              height={SCREEN_HEIGHT}
              rx={SCREEN_RADIUS}
              ry={SCREEN_RADIUS}
            />
          </clipPath>
        </defs>
      </svg>
    </div>
  );
}

export default function ProducerSettings() {
  const { t } = useTranslation("producerOrg");
  const { user, profile, refetchRole } = useAuth();
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    full_name: profile?.full_name || "",
    phone: profile?.phone || "",
    documento: profile?.document_number || (profile as { cnpj?: string | null })?.cnpj || profile?.cpf || "",
    birth_date: profile?.birth_date || "",
    cep: profile?.cep || "",
    street: profile?.street || "",
    address_number: profile?.address_number || "",
    complement: profile?.complement || "",
    neighborhood: profile?.neighborhood || "",
    city: profile?.city || "",
    state: profile?.state || "",
  });

  const handleCepChange = async (value: string) => {
    const cleanCep = value.replace(/\D/g, "");
    setForm((prev) => ({ ...prev, cep: value }));
    
    if (cleanCep.length === 8) {
      try {
        const res = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
        const data = await res.json();
        if (!data.erro) {
          setForm((prev) => ({
            ...prev,
            street: data.logradouro || prev.street,
            neighborhood: data.bairro || prev.neighborhood,
            city: data.localidade || prev.city,
            state: data.uf || prev.state,
          }));
        }
      } catch (err) {
        console.error("Erro ao buscar CEP", err);
      }
    }
  };

  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "organizer" | "notifications" | "integrations">("profile");
  const [localImageUrls, setLocalImageUrls] = useState<{
    avatar_url?: string;
    organizer_logo_url?: string;
    organizer_banner_url?: string;
  }>({});
  const [username, setUsername] = useState(profile?.organizer_slug || "");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [, setUsernameChecking] = useState(false);
  const usernameValidateRef = useRef<ReturnType<typeof setTimeout>>();
  const [organizerBio, setOrganizerBio] = useState(profile?.organizer_bio || "");
  const [organizerInstagram, setOrganizerInstagram] = useState(profile?.organizer_instagram || "");
  const [organizerFacebook, setOrganizerFacebook] = useState(profile?.organizer_facebook || "");
  const [organizerWebsite, setOrganizerWebsite] = useState(profile?.organizer_website || "");
  const [isSavingOrganizer, setIsSavingOrganizer] = useState(false);

  const [weeklyDigestEnabled, setWeeklyDigestEnabled] = useState<boolean>(true);
  const [isUpdatingDigest, setIsUpdatingDigest] = useState(false);
  const [isSendingTestDigest, setIsSendingTestDigest] = useState(false);

  useEffect(() => {
    if (profile) {
      setWeeklyDigestEnabled((profile as ProfileRecord).weekly_digest_enabled !== false);
    }
  }, [profile]);

  const handleToggleWeeklyDigest = async (checked: boolean) => {
    if (!user?.id) return;
    setWeeklyDigestEnabled(checked);
    setIsUpdatingDigest(true);
    try {
      await updateProfile(user.id, { weekly_digest_enabled: checked });
      toast({
        title: checked ? "Resumo semanal ativado" : "Resumo semanal desativado",
        description: checked
          ? "Você receberá seu e-mail toda sexta-feira às 19h30 com os resultados da semana."
          : "Você não receberá mais os e-mails de resumo semanal.",
      });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    } catch (err) {
      setWeeklyDigestEnabled(!checked);
      toast({
        title: "Erro ao atualizar preferência",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsUpdatingDigest(false);
    }
  };

  const handleSendTestWeeklyDigest = async () => {
    if (!user?.id) return;
    setIsSendingTestDigest(true);
    try {
      const { data, error } = await supabase.functions.invoke("producer-weekly-digest", {
        body: {
          testMode: true,
          producerId: user.id,
          recipientEmail: user.email,
        },
      });
      if (error) throw error;
      toast({
        title: "E-mail de teste enviado!",
        description: `Enviamos a prévia do resumo semanal para ${user.email}. Confira sua caixa de entrada!`,
      });
    } catch (err) {
      toast({
        title: "Erro ao enviar teste",
        description: err.message || "Não foi possível disparar o e-mail de teste.",
        variant: "destructive",
      });
    } finally {
      setIsSendingTestDigest(false);
    }
  };

  const avatarRef = useRef<HTMLInputElement>(null);
  const logoRef = useRef<HTMLInputElement>(null);
  const bannerRef = useRef<HTMLInputElement>(null);

  const saveMutation = useMutation({
    mutationFn: () => {
      const digits = form.documento.replace(/\D/g, "");
      const docValidation = validateDocument(digits);
      const isCnpj = docValidation.type === "cnpj";
      return updateProfile(user!.id, {
        full_name: form.full_name,
        phone: form.phone,
        document_number: docValidation.valid ? digits : null,
        document_type: docValidation.type || null,
        birth_date: isCnpj ? null : (form.birth_date || null),
        cep: form.cep,
        street: form.street,
        address_number: form.address_number,
        complement: form.complement,
        neighborhood: form.neighborhood,
        city: form.city,
        state: form.state,
      });
    },
    onSuccess: () => {
      toast({ title: t("settings.toasts.profileUpdated") });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
    onError: (err: Error) => toast({ title: t("settings.toasts.error"), description: err.message, variant: "destructive" }),
  });

  // Debounced auto-save for organizer page fields
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const debouncedUpdate = useCallback((field: string, value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setIsSavingOrganizer(true);
    debounceRef.current = setTimeout(() => {
      updateProfile(user!.id, { [field]: value } as Record<string, unknown>).then(() => {
        // saved quietly
      }).catch(() => {
        toast({ title: t("settings.toasts.saveError"), variant: "destructive" });
      }).finally(() => {
        setIsSavingOrganizer(false);
      });
    }, 800);
  }, [user, t]);

  const validateUsername = useCallback(async (value: string) => {
    const normalized = value.trim().toLowerCase();
    setUsername(normalized);
    setUsernameError(null);
    if (usernameValidateRef.current) clearTimeout(usernameValidateRef.current);
    if (!normalized) {
      setUsernameError(t("settings.toasts.usernameEmpty"));
      return;
    }
    if (!/^[a-z0-9_-]{3,30}$/.test(normalized)) {
      setUsernameError(t("settings.toasts.usernameInvalid"));
      return;
    }
    setUsernameChecking(true);
    usernameValidateRef.current = setTimeout(async () => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id")
          .eq("organizer_slug", normalized)
          .maybeSingle();
        if (error) throw error;
        if (data && data.id !== user?.id) {
          setUsernameError(t("settings.toasts.usernameTaken"));
        }
      } catch {
        setUsernameError(t("settings.toasts.usernameCheckFailed"));
      } finally {
        setUsernameChecking(false);
      }
    }, 500);
  }, [user?.id, t]);

  useEffect(() => {
    setUsername(profile?.organizer_slug || "");
    setOrganizerBio(profile?.organizer_bio || "");
    setOrganizerInstagram(profile?.organizer_instagram || "");
    setOrganizerFacebook(profile?.organizer_facebook || "");
    setOrganizerWebsite(profile?.organizer_website || "");
    if (profile) {
      setForm((p) => ({
        ...p,
        full_name: p.full_name || profile.full_name || "",
        phone: p.phone || profile.phone || "",
        documento: p.documento || profile.document_number || (profile as { cnpj?: string | null }).cnpj || profile.cpf || "",
        birth_date: p.birth_date || profile.birth_date || "",
        cep: p.cep || profile.cep || "",
        street: p.street || profile.street || "",
        address_number: p.address_number || profile.address_number || "",
        complement: p.complement || profile.complement || "",
        neighborhood: p.neighborhood || profile.neighborhood || "",
        city: p.city || profile.city || "",
        state: p.state || profile.state || "",
      }));
    }
  }, [profile]);

  const handleImageUpload = async (
    file: File,
    field: "avatar_url" | "organizer_logo_url" | "organizer_banner_url",
    setLoading: (v: boolean) => void,
  ) => {
    if (!user) return;
    
    setLoading(true);
    try {
      const maxWidth = field === "organizer_banner_url" ? 1200 : 400;
      let processedFile = file;
      try {
        processedFile = await convertImageToWebP(file, { maxWidth, quality: 0.8 });
      } catch (e) {
        console.error("Erro ao converter imagem do produtor para WebP:", e);
      }

      if (processedFile.size > 5 * 1024 * 1024) {
        toast({ title: t("settings.toasts.imageSizeError"), variant: "destructive" });
        setLoading(false);
        return;
      }

      const path = `${user.id}/${field}-${Date.now()}.webp`;
      const { data: upData, error: upErr } = await supabase.storage.from("event-images").upload(path, processedFile, { upsert: true });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("event-images").getPublicUrl(upData?.path || path);
      const publicUrl = `${data.publicUrl}?t=${Date.now()}`;
      await updateProfile(user.id, { [field]: publicUrl } as Record<string, unknown>);
      setLocalImageUrls((prev) => ({ ...prev, [field]: publicUrl }));
      toast({ title: t("settings.toasts.imageUpdated") });
      // small delay to mitigate read-replica lag before refetching
      await new Promise((r) => setTimeout(r, 400));
      await refetchRole();
      queryClient.invalidateQueries({ queryKey: ["profile"] });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: t("settings.toasts.imageError"), description: message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const initialDocumento = profile?.document_number || (profile as { cnpj?: string | null })?.cnpj || profile?.cpf || "";
  const hasProfileChanges =
    form.full_name !== (profile?.full_name || "")
    || form.phone !== (profile?.phone || "")
    || form.documento !== initialDocumento
    || form.birth_date !== (profile?.birth_date || "")
    || form.cep !== (profile?.cep || "")
    || form.street !== (profile?.street || "")
    || form.address_number !== (profile?.address_number || "")
    || form.complement !== (profile?.complement || "")
    || form.neighborhood !== (profile?.neighborhood || "")
    || form.city !== (profile?.city || "")
    || form.state !== (profile?.state || "");

  const isCnpj = detectDocumentType(sanitizeDocument(form.documento)) === "cnpj";

  return (
    <div className={panelPage}>
      {/* Header Solto */}
      <header className="space-y-1">
        <h1 className={panelTitle}>{t("settings.pageTitle")}</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          {t("settings.pageSubtitle")}
        </p>
      </header>

      {/* Abas em Pílula Apple */}
      <div className="w-full">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as typeof activeTab)}
          className="w-full space-y-6"
        >
          <div className="relative w-full overflow-x-auto pb-1 no-scrollbar touch-pan-x -mx-1 px-1">
            <TabsList className="flex h-auto w-max sm:w-fit justify-start rounded-full bg-secondary/70 p-1">
              <TabsTrigger
                value="profile"
                className={`${panelPill} h-11 sm:h-9 gap-2 px-4 text-xs sm:text-sm whitespace-nowrap min-w-[44px]`}
              >
                <User className="h-4 w-4 shrink-0" />
                <span>{t("settings.tabs.profile")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="organizer"
                className={`${panelPill} h-11 sm:h-9 gap-2 px-4 text-xs sm:text-sm whitespace-nowrap min-w-[44px]`}
              >
                <Globe className="h-4 w-4 shrink-0" />
                <span>{t("settings.tabs.organizer")}</span>
              </TabsTrigger>
              <TabsTrigger
                value="notifications"
                className={`${panelPill} h-11 sm:h-9 gap-2 px-4 text-xs sm:text-sm whitespace-nowrap min-w-[44px]`}
              >
                <Bell className="h-4 w-4 shrink-0" />
                <span>Notificações</span>
              </TabsTrigger>
              <TabsTrigger
                value="integrations"
                className={`${panelPill} h-11 sm:h-9 gap-2 px-4 text-xs sm:text-sm whitespace-nowrap min-w-[44px]`}
              >
                <Link className="h-4 w-4 shrink-0" />
                <span>{t("settings.tabs.integrations")}</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab: Perfil & Conta */}
          <TabsContent value="profile" className="mt-0 space-y-6">
            <div className="w-full space-y-6">
              {/* Foto de Perfil */}
              <div className={panelSurface}>
                <div className={panelSectionHeader}>
                  <div className="flex items-center gap-2">
                    <span className={panelAccentDot} />
                    <h2 className={panelSectionTitle}>{t("settings.profilePhoto.title")}</h2>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("settings.profilePhoto.description")}
                  </p>
                </div>
                <div className="p-5 sm:p-7">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
                    <div 
                      onClick={() => avatarRef.current?.click()}
                      className="group relative w-20 h-20 rounded-full bg-secondary/50 flex items-center justify-center overflow-hidden border border-border/70 hover:border-primary/50 cursor-pointer transition-all duration-200 shrink-0"
                      title={t("settings.profilePhoto.altTitle")}
                    >
                      {(localImageUrls.avatar_url || profile?.avatar_url) ? (
                        <img src={localImageUrls.avatar_url || profile?.avatar_url} alt={t("settings.profilePhoto.altAvatar")} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      ) : (
                        <span className="text-2xl font-bold font-display text-muted-foreground group-hover:scale-105 transition-transform duration-200">
                          {profile?.full_name?.[0]?.toUpperCase() || "?"}
                        </span>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-200">
                        <Camera className="h-5 w-5 text-white" />
                      </div>
                      {uploadingAvatar && (
                        <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <input ref={avatarRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                        onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f, "avatar_url", setUploadingAvatar); e.target.value = ""; }} />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => avatarRef.current?.click()} 
                        disabled={uploadingAvatar}
                        className={cn(panelPill, "h-11 sm:h-9 min-w-[44px] px-4 text-xs font-semibold gap-1.5")}
                      >
                        {uploadingAvatar ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                        {t("settings.profilePhoto.changePhoto")}
                      </Button>
                      <p className="text-xs text-muted-foreground">{t("settings.profilePhoto.hint")}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Aparência do Painel */}
              <div className={panelSurface}>
                <div className={panelSectionHeader}>
                  <div className="flex items-center gap-2">
                    <span className={panelAccentDot} />
                    <h2 className={panelSectionTitle}>{t("settings.appearance.title")}</h2>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("settings.appearance.description")}
                  </p>
                </div>
                <div className="p-5 sm:p-7">
                  <div className={cn(panelRow, "flex items-center justify-between p-4 sm:p-5")}>
                    <div className="space-y-0.5 pr-4">
                      <p className="text-sm font-medium text-foreground">{t("settings.appearance.themeMode")}</p>
                      <p className="text-xs text-muted-foreground">{t("settings.appearance.themeHint")}</p>
                    </div>
                    <AnimatedThemeToggler className="h-11 w-11 min-h-[44px] min-w-[44px] rounded-full border border-border/70 bg-secondary/50 hover:bg-secondary/80 transition-colors" />
                  </div>
                </div>
              </div>

              {/* Informações Pessoais */}
              <div className={panelSurface}>
                <div className={panelSectionHeader}>
                  <div className="flex items-center gap-2">
                    <span className={panelAccentDot} />
                    <h2 className={panelSectionTitle}>{t("settings.personalInfo.title")}</h2>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("settings.personalInfo.description")}
                  </p>
                </div>
                <div className="p-5 sm:p-7 space-y-5">
                  <div>
                    <DocumentInput
                      label={t("settings.personalInfo.cpfCnpj")}
                      value={form.documento}
                      onChange={(value) => setForm((p) => ({ ...p, documento: value }))}
                      className="h-11 sm:h-10 text-sm rounded-2xl border-border/60 bg-secondary/30 focus-visible:bg-background focus-visible:ring-1 focus-visible:ring-border/80 shadow-none font-medium"
                    />
                  </div>

                  <div>
                    <Label className={panelFieldLabel}>
                      {isCnpj ? t("settings.personalInfo.companyName") : t("settings.personalInfo.fullName")}
                    </Label>
                    <div className="relative flex items-center mt-1.5 rounded-2xl border border-border/60 bg-secondary/30 focus-within:border-border/80 focus-within:bg-background focus-within:ring-1 focus-within:ring-border/80 transition-all">
                      <span className="pl-3.5 pr-1 flex items-center text-muted-foreground shrink-0">
                        <User className="h-4 w-4" />
                      </span>
                      <Input 
                        value={form.full_name} 
                        onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
                        className="border-0 bg-transparent rounded-2xl focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-10 text-sm font-medium px-2.5 shadow-none w-full" 
                      />
                    </div>
                  </div>

                  {!isCnpj && (
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.personalInfo.birthDate")}</Label>
                      <div className="relative flex items-center mt-1.5 rounded-2xl border border-border/60 bg-secondary/30 focus-within:border-border/80 focus-within:bg-background focus-within:ring-1 focus-within:ring-border/80 transition-all">
                        <span className="pl-3.5 pr-1 flex items-center text-muted-foreground shrink-0">
                          <CalendarDays className="h-4 w-4" />
                        </span>
                        <Input 
                          type="date"
                          value={form.birth_date} 
                          onChange={(e) => setForm((p) => ({ ...p, birth_date: e.target.value }))}
                          className="border-0 bg-transparent rounded-2xl focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-10 text-sm font-medium px-2.5 shadow-none w-full" 
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className={panelFieldLabel}>{t("settings.personalInfo.phone")}</Label>
                    <div className="relative flex items-center mt-1.5 rounded-2xl border border-border/60 bg-secondary/30 focus-within:border-border/80 focus-within:bg-background focus-within:ring-1 focus-within:ring-border/80 transition-all">
                      <span className="pl-3.5 pr-1 flex items-center text-muted-foreground shrink-0">
                        <Smartphone className="h-4 w-4" />
                      </span>
                      <Input 
                        value={form.phone} 
                        onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} 
                        placeholder={t("settings.personalInfo.phonePlaceholder")}
                        className="border-0 bg-transparent rounded-2xl focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-10 text-sm font-medium px-2.5 shadow-none w-full" 
                      />
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground pt-2 border-t border-border/50">
                    {t("settings.personalInfo.billingNote")}
                  </p>
                </div>
              </div>

              {/* Endereço de Faturamento */}
              <div className={panelSurface}>
                <div className={panelSectionHeader}>
                  <div className="flex items-center gap-2">
                    <span className={panelAccentDot} />
                    <h2 className={panelSectionTitle}>{t("settings.billingAddress.title")}</h2>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t("settings.billingAddress.description")}
                  </p>
                </div>
                <div className="p-5 sm:p-7 space-y-4 sm:space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.billingAddress.cep")}</Label>
                      <div className="relative flex items-center mt-1.5 rounded-2xl border border-border/60 bg-secondary/30 focus-within:border-border/80 focus-within:bg-background focus-within:ring-1 focus-within:ring-border/80 transition-all">
                        <span className="pl-3.5 pr-1 flex items-center text-muted-foreground shrink-0">
                          <MapPin className="h-4 w-4" />
                        </span>
                        <Input 
                          value={form.cep} 
                          onChange={(e) => handleCepChange(e.target.value)}
                          placeholder={t("settings.billingAddress.cepPlaceholder")}
                          className="border-0 bg-transparent rounded-2xl focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-10 text-sm font-medium px-2.5 shadow-none w-full" 
                        />
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <Label className={panelFieldLabel}>{t("settings.billingAddress.street")}</Label>
                      <Input 
                        value={form.street} 
                        onChange={(e) => setForm((p) => ({ ...p, street: e.target.value }))}
                        placeholder={t("settings.billingAddress.streetPlaceholder")}
                        className={cn(panelControl, "mt-1.5 h-11 sm:h-10 text-sm")} 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.billingAddress.number")}</Label>
                      <Input 
                        value={form.address_number} 
                        onChange={(e) => setForm((p) => ({ ...p, address_number: e.target.value }))}
                        placeholder={t("settings.billingAddress.numberPlaceholder")}
                        className={cn(panelControl, "mt-1.5 h-11 sm:h-10 text-sm")} 
                      />
                    </div>
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.billingAddress.complement")}</Label>
                      <Input 
                        value={form.complement} 
                        onChange={(e) => setForm((p) => ({ ...p, complement: e.target.value }))}
                        placeholder={t("settings.billingAddress.complementPlaceholder")}
                        className={cn(panelControl, "mt-1.5 h-11 sm:h-10 text-sm")} 
                      />
                    </div>
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.billingAddress.neighborhood")}</Label>
                      <Input 
                        value={form.neighborhood} 
                        onChange={(e) => setForm((p) => ({ ...p, neighborhood: e.target.value }))}
                        placeholder={t("settings.billingAddress.neighborhoodPlaceholder")}
                        className={cn(panelControl, "mt-1.5 h-11 sm:h-10 text-sm")} 
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.billingAddress.city")}</Label>
                      <Input 
                        value={form.city} 
                        onChange={(e) => setForm((p) => ({ ...p, city: e.target.value }))}
                        placeholder={t("settings.billingAddress.cityPlaceholder")}
                        className={cn(panelControl, "mt-1.5 h-11 sm:h-10 text-sm")} 
                      />
                    </div>
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.billingAddress.state")}</Label>
                      <Input 
                        value={form.state} 
                        onChange={(e) => setForm((p) => ({ ...p, state: e.target.value }))}
                        placeholder={t("settings.billingAddress.statePlaceholder")}
                        className={cn(panelControl, "mt-1.5 h-11 sm:h-10 text-sm")} 
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Tab: Página do Organizador */}
          <TabsContent value="organizer" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Form Column */}
              <div className="w-full lg:col-span-7 space-y-6">
                
                {/* Identidade Visual */}
                <div className={panelSurface}>
                  <div className={panelSectionHeader}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className={panelAccentDot} />
                          <h2 className={panelSectionTitle}>
                            {t("settings.visualIdentity.title")}
                          </h2>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {t("settings.visualIdentity.description")}
                        </p>
                      </div>
                      {isSavingOrganizer && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-secondary/80 px-2.5 py-1 rounded-full animate-fade-in">
                          <Loader2 className="h-3 w-3 animate-spin text-primary" />
                          <span>{t("settings.visualIdentity.saving")}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-5 sm:p-7 space-y-6">
                    {/* Integrated Banner & Logo Upload Layout */}
                    <div className="relative pb-10">
                      {/* Banner Wrapper */}
                      <div 
                        onClick={() => bannerRef.current?.click()}
                        className="relative w-full aspect-[3/1] rounded-2xl overflow-hidden border border-dashed border-border/80 bg-secondary/20 flex items-center justify-center cursor-pointer group hover:border-primary/50 transition-all duration-200"
                        title={t("settings.visualIdentity.clickToChangeBanner")}
                      >
                        {(localImageUrls.organizer_banner_url || profile?.organizer_banner_url) ? (
                          <>
                            <img 
                              src={localImageUrls.organizer_banner_url || profile?.organizer_banner_url} 
                              alt={t("settings.visualIdentity.altBanner")} 
                              className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300" 
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-200">
                              <div className="flex items-center gap-1.5 bg-background/90 text-foreground text-xs font-semibold px-3.5 py-2 rounded-full shadow-md">
                                <Camera className="h-3.5 w-3.5" />
                                <span>{t("settings.visualIdentity.changeBanner")}</span>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="flex flex-col items-center text-muted-foreground p-4 text-center group-hover:text-foreground transition-colors">
                            <ImageIcon className="h-8 w-8 mb-1.5 opacity-60 group-hover:scale-110 transition-transform duration-200" />
                            <span className="text-xs font-semibold">{t("settings.visualIdentity.bannerClick")}</span>
                            <span className="text-[10px] opacity-75 mt-0.5">{t("settings.visualIdentity.bannerRecommended")}</span>
                          </div>
                        )}
                        
                        {uploadingBanner && (
                          <div className="absolute inset-0 bg-background/80 backdrop-blur-xs flex items-center justify-center">
                            <Loader2 className="h-6 w-6 animate-spin text-primary" />
                          </div>
                        )}
                      </div>

                      {/* Overlapped Logo Wrapper */}
                      <div className="absolute bottom-2 left-6 z-10">
                        <div 
                          onClick={() => logoRef.current?.click()}
                          className="relative w-20 h-20 rounded-2xl border-4 border-card bg-secondary/40 shadow-md overflow-hidden flex items-center justify-center cursor-pointer group hover:border-primary/50 transition-all duration-200"
                          title={t("settings.visualIdentity.clickToChangeLogo")}
                        >
                          {(localImageUrls.organizer_logo_url || profile?.organizer_logo_url) ? (
                            <>
                              <img 
                                src={localImageUrls.organizer_logo_url || profile?.organizer_logo_url} 
                                alt={t("settings.visualIdentity.altLogo")} 
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                              />
                              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-200">
                                <Camera className="h-4 w-4 text-white" />
                              </div>
                            </>
                          ) : (
                            <div className="flex flex-col items-center text-muted-foreground group-hover:text-foreground">
                              <ImageIcon className="h-6 w-6 opacity-60" />
                            </div>
                          )}
                          
                          {uploadingLogo && (
                            <div className="absolute inset-0 bg-background/80 backdrop-blur-xs flex items-center justify-center">
                              <Loader2 className="h-4 w-4 animate-spin text-primary" />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action buttons and guidelines */}
                    <div className="flex flex-wrap items-center gap-3 pt-1">
                      <input 
                        ref={logoRef} 
                        type="file" 
                        accept="image/jpeg,image/png,image/webp" 
                        className="hidden"
                        onChange={(e) => { 
                          const f = e.target.files?.[0]; 
                          if (f) handleImageUpload(f, "organizer_logo_url", setUploadingLogo); 
                          e.target.value = ""; 
                        }} 
                      />
                      <input 
                        ref={bannerRef} 
                        type="file" 
                        accept="image/jpeg,image/png,image/webp" 
                        className="hidden"
                        onChange={(e) => { 
                          const f = e.target.files?.[0]; 
                          if (f) handleImageUpload(f, "organizer_banner_url", setUploadingBanner); 
                          e.target.value = ""; 
                        }} 
                      />
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => logoRef.current?.click()} 
                        disabled={uploadingLogo}
                        className={cn(panelPill, "h-11 sm:h-9 min-w-[44px] px-4 text-xs font-semibold gap-1.5")}
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {t("settings.visualIdentity.changeLogo")}
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => bannerRef.current?.click()} 
                        disabled={uploadingBanner}
                        className={cn(panelPill, "h-11 sm:h-9 min-w-[44px] px-4 text-xs font-semibold gap-1.5")}
                      >
                        <Upload className="h-3.5 w-3.5" />
                        {t("settings.visualIdentity.changeBanner")}
                      </Button>
                      <span className="text-[11px] text-muted-foreground self-center ml-auto">
                        {t("settings.visualIdentity.uploadHint")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Perfil & Informações */}
                <div className={panelSurface}>
                  <div className={panelSectionHeader}>
                    <div className="flex items-center gap-2">
                      <span className={panelAccentDot} />
                      <h2 className={panelSectionTitle}>{t("settings.basicInfo.title")}</h2>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("settings.basicInfo.description")}
                    </p>
                  </div>
                  <div className="p-5 sm:p-7 space-y-5">
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.basicInfo.publicUrlLabel")}</Label>
                      <div className="relative flex items-center mt-1.5 rounded-2xl border border-border/60 bg-secondary/30 focus-within:border-border/80 focus-within:bg-background focus-within:ring-1 focus-within:ring-border/80 overflow-hidden transition-all">
                        <span className="bg-secondary/60 px-3.5 flex items-center text-xs font-medium text-muted-foreground border-r border-border/50 select-none h-11 sm:h-10">
                          {t("settings.basicInfo.urlPrefix")}
                        </span>
                        <Input
                          value={username}
                          onChange={(e) => validateUsername(e.target.value)}
                          placeholder={t("settings.basicInfo.usernamePlaceholder")}
                          onBlur={(e) => debouncedUpdate("organizer_slug", e.target.value.trim().toLowerCase())}
                          className="border-0 bg-transparent rounded-none focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-10 text-sm font-medium px-3 shadow-none flex-1"
                        />
                      </div>
                      {usernameError ? (
                        <p className="text-xs text-destructive mt-1.5 font-medium">{usernameError}</p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1.5">{t("settings.basicInfo.usernameHint")}</p>
                      )}
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1.5">
                        <Label className={panelFieldLabel}>{t("settings.basicInfo.bioLabel")}</Label>
                        <span className="text-[11px] text-muted-foreground tabular-nums">
                          {organizerBio.length}/300
                        </span>
                      </div>
                      <Textarea
                        value={organizerBio}
                        onChange={(e) => {
                          const val = e.target.value.slice(0, 300);
                          setOrganizerBio(val);
                          debouncedUpdate("organizer_bio", val);
                        }}
                        placeholder={t("settings.basicInfo.bioPlaceholder")}
                        className="min-h-[110px] resize-none text-sm rounded-2xl border-border/60 bg-secondary/30 focus-visible:bg-background focus-visible:ring-1 focus-visible:ring-border/80 p-3.5 font-medium shadow-none leading-relaxed"
                      />
                    </div>
                  </div>
                </div>

                {/* Redes Sociais */}
                <div className={panelSurface}>
                  <div className={panelSectionHeader}>
                    <div className="flex items-center gap-2">
                      <span className={panelAccentDot} />
                      <h2 className={panelSectionTitle}>{t("settings.socialMedia.title")}</h2>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {t("settings.socialMedia.description")}
                    </p>
                  </div>
                  <div className="p-5 sm:p-7 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      
                      {/* Instagram */}
                      <div>
                        <Label className={panelFieldLabel}>{t("settings.socialMedia.instagramLabel")}</Label>
                        <div className="relative flex items-center mt-1.5 rounded-2xl border border-border/60 bg-secondary/30 focus-within:border-border/80 focus-within:bg-background focus-within:ring-1 focus-within:ring-border/80 transition-all">
                          <span className="pl-3.5 pr-1 flex items-center text-muted-foreground shrink-0">
                            <Instagram className="h-4 w-4" />
                          </span>
                          <Input
                            value={organizerInstagram}
                            onChange={(e) => {
                              setOrganizerInstagram(e.target.value);
                              debouncedUpdate("organizer_instagram", e.target.value);
                            }}
                            placeholder="https://instagram.com/username"
                            className="border-0 bg-transparent rounded-2xl focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-10 text-sm font-medium px-2.5 shadow-none w-full"
                          />
                        </div>
                      </div>

                      {/* Facebook */}
                      <div>
                        <Label className={panelFieldLabel}>{t("settings.socialMedia.facebookLabel")}</Label>
                        <div className="relative flex items-center mt-1.5 rounded-2xl border border-border/60 bg-secondary/30 focus-within:border-border/80 focus-within:bg-background focus-within:ring-1 focus-within:ring-border/80 transition-all">
                          <span className="pl-3.5 pr-1 flex items-center text-muted-foreground shrink-0">
                            <Facebook className="h-4 w-4" />
                          </span>
                          <Input
                            value={organizerFacebook}
                            onChange={(e) => {
                              setOrganizerFacebook(e.target.value);
                              debouncedUpdate("organizer_facebook", e.target.value);
                            }}
                            placeholder="https://facebook.com/pagina"
                            className="border-0 bg-transparent rounded-2xl focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-10 text-sm font-medium px-2.5 shadow-none w-full"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Website */}
                    <div>
                      <Label className={panelFieldLabel}>{t("settings.socialMedia.websiteLabel")}</Label>
                      <div className="relative flex items-center mt-1.5 rounded-2xl border border-border/60 bg-secondary/30 focus-within:border-border/80 focus-within:bg-background focus-within:ring-1 focus-within:ring-border/80 transition-all">
                        <span className="pl-3.5 pr-1 flex items-center text-muted-foreground shrink-0">
                          <Globe className="h-4 w-4" />
                        </span>
                        <Input
                          value={organizerWebsite}
                          onChange={(e) => {
                            setOrganizerWebsite(e.target.value);
                            debouncedUpdate("organizer_website", e.target.value);
                          }}
                          placeholder="https://www.meusite.com"
                          className="border-0 bg-transparent rounded-2xl focus-visible:ring-0 focus-visible:ring-offset-0 h-11 sm:h-10 text-sm font-medium px-2.5 shadow-none w-full"
                        />
                      </div>
                    </div>

                    {profile?.organizer_slug && (
                      <div className="pt-3 flex justify-between items-center border-t border-border/50">
                        <a 
                          href={`/produtor/${profile.organizer_slug}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="inline-flex items-center gap-1.5 text-xs text-primary font-semibold hover:underline min-h-[44px] py-2"
                        >
                          {t("settings.basicInfo.viewPublicPage")}
                          <ArrowUpRight className="h-3.5 w-3.5" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Preview Column: Mobile stack, desktop sticky */}
              <div className="w-full lg:col-span-5 flex flex-col items-center justify-center lg:sticky lg:top-6 pt-2 lg:pt-0">
                <Iphone className="w-full max-w-[280px] sm:max-w-[310px] mx-auto select-none shadow-2xl">
                  {/* Screen Content Wrapper */}
                  <div className="w-full h-full bg-background relative flex flex-col">
                    {/* Mock Status Bar - Floating with white text and gradient mask */}
                    <div className="absolute top-0 left-0 right-0 z-30 flex justify-between items-center px-5 pt-2.5 pb-4 text-[9px] text-white font-bold tracking-tight bg-gradient-to-b from-black/40 via-black/10 to-transparent">
                      <span>09:41</span>
                      <div className="flex items-center gap-1">
                        <span>5G</span>
                        <div className="w-4 h-2 border border-white/60 rounded-sm p-px flex items-center">
                          <div className="h-full w-[80%] bg-white rounded-2xs" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Content Area */}
                    <div className="flex-1 overflow-y-auto no-scrollbar pb-6 text-xs text-foreground">
                      {/* Banner Mock - Height adjusted to cover status bar/notch nicely */}
                      <div className="relative h-28 bg-muted overflow-hidden flex items-center justify-center">
                        {(localImageUrls.organizer_banner_url || profile?.organizer_banner_url) ? (
                          <img 
                            src={localImageUrls.organizer_banner_url || profile?.organizer_banner_url} 
                            alt="Banner Preview" 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <div className="absolute inset-0 bg-gradient-to-tr from-violet-600/30 via-primary/10 to-accent/5" />
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
                      </div>
                      
                      {/* Profile Profile Header */}
                      <div className="px-4 -mt-7 relative z-10 flex flex-col items-center text-center">
                        {/* Logo Circle Mock */}
                        <div className="h-14 w-14 rounded-full border-2 border-background bg-card overflow-hidden shadow-md flex items-center justify-center">
                          {(localImageUrls.organizer_logo_url || profile?.organizer_logo_url) ? (
                            <img 
                              src={localImageUrls.organizer_logo_url || profile?.organizer_logo_url} 
                              alt="Logo Preview" 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            <div className="h-full w-full bg-primary/10 text-primary flex items-center justify-center font-bold text-base font-display">
                              {(profile?.full_name || "P")[0].toUpperCase()}
                            </div>
                          )}
                        </div>
                        
                        {/* Profile Info */}
                        <h4 className="font-display font-bold text-xs mt-2 text-foreground truncate max-w-[200px]">
                          {profile?.full_name || t("settings.preview.yourOrganizer")}
                        </h4>
                        <span className="text-[9px] text-muted-foreground">@{username || "username"}</span>
                        
                        {/* Fake Stats */}
                        <div className={cn(panelEyebrow, "flex items-center gap-1.5 mt-1 text-[9px] bg-muted/60 px-2 py-0.5 rounded-full normal-case tracking-normal")}>
                          <span className="font-semibold text-foreground">0</span> {t("settings.preview.followers")}
                        </div>
                        
                        {/* Dynamic Social Icons */}
                        <div className="flex gap-2 mt-2.5 justify-center">
                          {organizerWebsite && (
                            <div className="p-1 rounded-full bg-muted/80 text-muted-foreground">
                              <Globe className="h-3 w-3" />
                            </div>
                          )}
                          {organizerInstagram && (
                            <div className="p-1 rounded-full bg-muted/80 text-muted-foreground">
                              <Instagram className="h-3 w-3" />
                            </div>
                          )}
                          {organizerFacebook && (
                            <div className="p-1 rounded-full bg-muted/80 text-muted-foreground">
                              <Facebook className="h-3 w-3" />
                            </div>
                          )}
                        </div>
                        
                        {/* Buttons Mock */}
                        <div className="flex gap-1.5 mt-3.5 w-full">
                          <div className="flex-1 text-[9px] h-6 bg-primary text-primary-foreground font-bold rounded-md flex items-center justify-center shadow-xs">
                            {t("settings.preview.followButton")}
                          </div>
                          <div className="flex-1 text-[9px] h-6 border border-border bg-background text-foreground font-bold rounded-md flex items-center justify-center">
                            {t("settings.preview.contactButton")}
                          </div>
                        </div>
                      </div>

                      {/* Bio Description */}
                      {organizerBio ? (
                        <div className="mt-4 px-3 text-center">
                          <p className="text-[10px] text-muted-foreground leading-snug whitespace-pre-line bg-muted/30 p-2 rounded-lg border border-border/40">
                            {organizerBio}
                          </p>
                        </div>
                      ) : (
                        <div className="mt-4 px-3 text-center">
                          <p className="text-[9px] text-muted-foreground/60 italic leading-snug bg-muted/10 p-2 rounded-lg border border-dashed border-border/50">
                            {t("settings.preview.bioEmpty")}
                          </p>
                        </div>
                      )}
                      
                      {/* Events area tab mockup */}
                      <div className="mt-5 border-t border-border/50 pt-2.5">
                        <div className="flex justify-center gap-3 text-[10px] text-muted-foreground border-b border-border/20 pb-1.5 px-3">
                          <span className="font-semibold text-foreground border-b border-foreground pb-0.5">{t("settings.preview.eventsAvailable")}</span>
                          <span>{t("settings.preview.eventsClosed")}</span>
                        </div>
                        
                        <div className="px-3 py-3 text-center">
                          <div className="border border-dashed border-border/70 rounded-lg p-3 flex flex-col items-center bg-muted/10">
                            <CalendarDays className="h-4 w-4 text-muted-foreground/50 mb-1" />
                            <span className="text-[9px] text-muted-foreground/80 font-medium">{t("settings.preview.eventsEmpty")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Iphone>
              </div>
            </div>
          </TabsContent>

          {/* Tab: Notificações */}
          <TabsContent value="notifications" className="mt-0 space-y-6">
            <div className="w-full max-w-3xl space-y-6">
              <div className={panelSurface}>
                <div className={panelSectionHeader}>
                  <div className="flex items-center gap-2">
                    <span className={panelAccentDot} />
                    <h2 className={panelSectionTitle}>Resumo Semanal de Desempenho</h2>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Relatório executivo consolidado com faturamento, ingressos vendidos e próximos eventos.
                  </p>
                </div>
                <div className="p-5 sm:p-7 space-y-5">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between rounded-xl border border-border/60 bg-muted/20 p-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="weekly-digest" className="text-sm font-semibold cursor-pointer">
                          Receber Resumo Semanal por E-mail
                        </Label>
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                          Toda sexta-feira às 19h30 BRT
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed max-w-xl">
                        Enviaremos um e-mail com o fechamento dos seus últimos 7 dias: receita bruta/líquida, comparativo de crescimento, principais eventos, agenda do final de semana e saldo disponível para saque.
                      </p>
                    </div>
                    <Switch
                      id="weekly-digest"
                      checked={weeklyDigestEnabled}
                      onCheckedChange={handleToggleWeeklyDigest}
                      disabled={isUpdatingDigest}
                    />
                  </div>

                  <div className="rounded-xl border border-border/60 bg-card p-4 space-y-3">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
                          <Send className="h-3.5 w-3.5 text-muted-foreground" />
                          Testar Envio do Resumo
                        </h4>
                        <p className="mt-1 text-xs text-muted-foreground">
                          Deseja conferir como o e-mail chega na sua caixa de entrada? Dispare uma prévia de teste com os dados reais dos seus eventos para <strong className="text-foreground">{user?.email}</strong>.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSendTestWeeklyDigest}
                        disabled={isSendingTestDigest}
                        className="shrink-0 h-9 font-medium"
                      >
                        {isSendingTestDigest ? (
                          <>
                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="mr-1.5 h-3.5 w-3.5" />
                            Enviar e-mail de teste
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-start gap-2.5 rounded-lg bg-muted/40 p-3 text-xs text-muted-foreground border border-border/40">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                    <span>
                      O envio automático ocorre via <strong>pg_cron</strong> diretamente pela infraestrutura da TicketHall, sem impactar o desempenho do seu painel e com total garantia de entrega.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Tab: Integrações & Webhooks */}
          <TabsContent value="integrations" className="mt-0">
            <div className="w-full">
              <WebhooksManager />
            </div>
          </TabsContent>

          {/* Barra Flutuante de Salvamento com Estilo Apple Pill */}
          {activeTab === "profile" && hasProfileChanges && (
            <div className="sticky bottom-4 z-20 mt-6 rounded-[24px] border border-border/70 bg-card/95 p-3.5 sm:px-5 sm:py-3 backdrop-blur-md shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs sm:text-sm text-muted-foreground font-medium">
                  {t("settings.saveBar.pendingChanges")}
                </p>
                <div className="flex items-center gap-2.5">
                  <Button
                    variant="outline"
                    disabled={!hasProfileChanges || saveMutation.isPending}
                    onClick={() => setForm({
                      full_name: profile?.full_name || "",
                      phone: profile?.phone || "",
                      documento: profile?.document_number || (profile as { cnpj?: string | null })?.cnpj || profile?.cpf || "",
                      birth_date: profile?.birth_date || "",
                      cep: profile?.cep || "",
                      street: profile?.street || "",
                      address_number: profile?.address_number || "",
                      complement: profile?.complement || "",
                      neighborhood: profile?.neighborhood || "",
                      city: profile?.city || "",
                      state: profile?.state || "",
                    })}
                    className={cn(panelPill, "min-h-[44px] sm:min-h-[40px] px-5 text-sm font-semibold")}
                  >
                    {t("settings.saveBar.cancel")}
                  </Button>
                  <Button 
                    onClick={() => saveMutation.mutate()} 
                    disabled={saveMutation.isPending || !hasProfileChanges} 
                    className={cn(panelPill, "min-h-[44px] sm:min-h-[40px] px-6 text-sm font-semibold shadow-xs active:scale-[0.98]")}
                  >
                    {saveMutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {t("settings.saveBar.save")}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Tabs>
      </div>
    </div>
  );
}
